import { Device, Call } from '@twilio/voice-sdk';
import debug from 'debug';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { AudioWidget } from '@/newComponents/AudioWidget';
// Widget state event types
var WidgetStateEvent;
(function (WidgetStateEvent) {
    WidgetStateEvent["AUDIO_LEVEL"] = "audio-level";
    WidgetStateEvent["WARNING"] = "warning";
    WidgetStateEvent["WARNING_CLEARED"] = "warning-cleared";
    WidgetStateEvent["REMOTE_AUDIO"] = "remote-audio";
    WidgetStateEvent["ERROR"] = "error";
})(WidgetStateEvent || (WidgetStateEvent = {}));
// Warning message mappings for call quality issues
const CALL_QUALITY_WARNINGS = {
    'high-rtt': 'High network latency detected',
    'low-mos': 'Poor call quality detected',
    'high-jitter': 'Network instability detected',
    'high-packet-loss': 'Packet loss detected',
    'constant-audio-input-level': 'Microphone may be muted',
    'constant-audio-output-level': 'Remote audio unchanged',
};
const REMOTE_AUDIO_THRESHOLD = 0.02;
const NOISE_GATE_THRESHOLD = 0.01; // Minimum level to register as audio activity
/**
 * Handles audio level monitoring for voice activity detection
 */
class AudioLevelMonitor {
    constructor(onLevelUpdate, logger) {
        this.analyserNode = null;
        this.localAudioSource = null;
        this.audioLevelAnimationId = null;
        this.audioLevelContext = null;
        this.onLevelUpdate = onLevelUpdate;
        this.log = logger;
    }
    start(localStream) {
        try {
            // Create a separate audio context for level monitoring
            this.audioLevelContext = new AudioContext();
            this.localAudioSource =
                this.audioLevelContext.createMediaStreamSource(localStream);
            this.analyserNode = this.audioLevelContext.createAnalyser();
            // Configure analyser for voice detection
            this.analyserNode.fftSize = 256;
            this.analyserNode.smoothingTimeConstant = 0.3;
            // Connect source to analyser
            this.localAudioSource.connect(this.analyserNode);
            // Create buffer for audio data
            const dataArray = new Float32Array(this.analyserNode.fftSize);
            const checkAudioLevel = () => {
                if (!this.analyserNode) {
                    this.stop();
                    return;
                }
                // Get time domain data
                this.analyserNode.getFloatTimeDomainData(dataArray);
                // Calculate RMS (Root Mean Square) for volume
                let sum = 0;
                for (const amplitude of dataArray) {
                    sum += amplitude * amplitude;
                }
                const rms = Math.sqrt(sum / dataArray.length);
                // Normalize to 0-1 range (adjust multiplier based on testing)
                const normalizedLevel = Math.min(1, rms * 10);
                // Apply noise gate threshold
                const gatedLevel = normalizedLevel > NOISE_GATE_THRESHOLD ? normalizedLevel : 0;
                // Emit audio level update
                this.onLevelUpdate(gatedLevel.toFixed(3));
                // Continue monitoring
                this.audioLevelAnimationId = requestAnimationFrame(checkAudioLevel);
            };
            // Start monitoring
            checkAudioLevel();
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            this.log(`Failed to start audio level monitoring: ${message}`, error);
        }
    }
    stop() {
        if (this.audioLevelAnimationId !== null) {
            cancelAnimationFrame(this.audioLevelAnimationId);
            this.audioLevelAnimationId = null;
        }
        if (this.localAudioSource) {
            try {
                this.localAudioSource.disconnect();
            }
            catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error';
                this.log(`Error disconnecting audio source: ${message}`, error);
            }
            this.localAudioSource = null;
        }
        if (this.audioLevelContext) {
            void this.audioLevelContext.close().catch((error) => {
                const message = error instanceof Error ? error.message : 'Unknown error';
                this.log(`Error closing audio level context: ${message}`, error);
            });
            this.audioLevelContext = null;
        }
        this.analyserNode = null;
        // Send zero level to clear the indicator
        this.onLevelUpdate('0');
    }
}
/**
 * AudioController manages the Twilio Voice connection and audio widget UI
 */
export class AudioController {
    constructor(config) {
        this.twilioDevice = null;
        this.activeCall = null;
        this.iceServers = [];
        this.widgetContainer = null;
        this.widgetRoot = null;
        this.audioMonitor = null;
        this.eventListeners = new Map();
        this.config = {
            host: config.host || 'https://channels.moveo.ai',
            integrationId: config.integrationId,
        };
        this.log = debug(`moveo:audio:controller:${config.integrationId}`);
    }
    /**
     * Initialize the audio widget
     */
    async init() {
        // Validate integration before initializing widget
        await this.validateIntegration();
        // Create and mount widget container
        this.createWidgetContainer();
        // Render the AudioWidget React component
        this.renderWidget();
        this.log('AudioController initialized');
        return this;
    }
    /**
     * Validate the integration exists and is accessible
     */
    async validateIntegration() {
        // Try new twiliovoice endpoint first
        const twilioVoiceUrl = `${this.config.host}/v1/twiliovoice/${encodeURIComponent(this.config.integrationId)}`;
        try {
            const response = await fetch(twilioVoiceUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                this.log('Integration validated using twiliovoice endpoint');
                return;
            }
            // If twiliovoice endpoint returns 404, try legacy web endpoint
            if (response.status === 404) {
                this.log('Twiliovoice endpoint not found, trying legacy web endpoint');
                const webUrl = `${this.config.host}/v1/web/${encodeURIComponent(this.config.integrationId)}`;
                const webResponse = await fetch(webUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                if (webResponse.ok) {
                    this.log('Integration validated using legacy web endpoint (backward compatibility)');
                    return;
                }
                if (webResponse.status === 404) {
                    throw new Error(`Integration ID "${this.config.integrationId}" was not found.`);
                }
                throw new Error(`Unable to validate integration (status ${webResponse.status} ${webResponse.statusText}).`);
            }
            // Non-404 error from twiliovoice endpoint
            throw new Error(`Unable to validate integration (status ${response.status} ${response.statusText}).`);
        }
        catch (error) {
            if (error instanceof Error) {
                // Re-throw with context if it's already an Error
                if (error.message.includes('Integration ID') ||
                    error.message.includes('Unable to validate')) {
                    throw error;
                }
                // Network or other errors
                throw new Error(`Failed to validate integration "${this.config.integrationId}": ${error.message}`);
            }
            throw new Error(`Failed to validate integration "${this.config.integrationId}": Unknown error`);
        }
    }
    /**
     * Create the DOM container for the audio widget
     */
    createWidgetContainer() {
        this.widgetContainer = document.createElement('div');
        this.widgetContainer.id = 'moveo-audio-widget';
        this.widgetContainer.style.position = 'fixed';
        this.widgetContainer.style.zIndex = '2147483647'; // Max z-index
        this.widgetContainer.style.bottom = '20px';
        this.widgetContainer.style.right = '20px';
        // Apply dark class based on system preference and listen for changes
        this.applyDarkMode();
        document.body.appendChild(this.widgetContainer);
    }
    /**
     * Sync the `dark` class on the widget container with the system color scheme.
     */
    applyDarkMode() {
        if (!this.widgetContainer)
            return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const sync = (dark) => {
            var _a;
            (_a = this.widgetContainer) === null || _a === void 0 ? void 0 : _a.classList.toggle('dark', dark);
        };
        sync(mq.matches);
        mq.addEventListener('change', (e) => sync(e.matches));
    }
    /**
     * Render the AudioWidget React component
     */
    renderWidget() {
        if (!this.widgetContainer) {
            throw new Error('Widget container not created');
        }
        this.widgetRoot = createRoot(this.widgetContainer);
        this.widgetRoot.render(createElement(AudioWidget, {
            onStartCall: this.startCall.bind(this),
            onEndCall: this.endCall.bind(this),
            onToggleMute: this.toggleMute.bind(this),
            isMuted: this.isMuted.bind(this),
            onSendDigits: this.sendDigits.bind(this),
            onEvent: this.on.bind(this),
        }));
    }
    /**
     * Get access token from backend
     */
    async getAccessToken() {
        try {
            const tokenUrl = `${this.config.host}/v1/twiliovoice/${this.config.integrationId}/token`;
            const response = await fetch(tokenUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                throw new Error(`Failed to get token: ${response.status} ${response.statusText}`);
            }
            const data = (await response.json());
            if (!data.token) {
                throw new Error('Token not found in response');
            }
            this.log('Token obtained', { identity: data.identity });
            return data;
        }
        catch (error) {
            this.handleError(error, 'Token fetch failed');
            throw error;
        }
    }
    /**
     * Initialize Twilio Device
     */
    async initializeTwilioDevice() {
        try {
            const token = await this.getAccessToken();
            // Store the iceServers for later use in connect()
            // To check ICE connection type, search logs for:
            //   "typ relay" = TURN, "typ srflx" = STUN, "typ host" = Direct
            if (token.ice_servers) {
                this.iceServers = token.ice_servers;
            }
            const deviceConfig = {
                // PCMU: no server-side transcode (pass-through). Opus: better packet loss resilience.
                codecPreferences: [Call.Codec.PCMU, Call.Codec.Opus],
                logLevel: 1, // DEBUG level
            };
            this.twilioDevice = new Device(token.token, deviceConfig);
            // Set up event listeners
            this.setupDeviceEventListeners();
            this.log('Twilio Device initialized with browser default audio settings');
            return this.twilioDevice;
        }
        catch (error) {
            this.handleError(error, 'Twilio Device initialization failed');
            throw error;
        }
    }
    /**
     * Setup Twilio Device event listeners (simplified for outbound calls only)
     */
    setupDeviceEventListeners() {
        if (!this.twilioDevice) {
            return;
        }
        // Handle device errors
        this.twilioDevice.on('error', (error) => {
            const message = this.handleError(error, 'Device error');
            this.emit(WidgetStateEvent.ERROR, message);
        });
        // Track when call disconnects
        this.twilioDevice.on('disconnect', (call) => {
            var _a;
            this.log('Call disconnected', { callSid: (_a = call === null || call === void 0 ? void 0 : call.parameters) === null || _a === void 0 ? void 0 : _a.CallSid });
            this.activeCall = null;
        });
    }
    /**
     * Setup call-specific event listeners
     */
    setupCallEventListeners(call) {
        call.on('accept', () => {
            this.log('Call accepted');
            // Start audio level monitoring for voice activity indication
            const localStream = call.getLocalStream();
            if (localStream) {
                // Create audio monitor with callback to update widget
                this.audioMonitor = new AudioLevelMonitor((level) => this.emit(WidgetStateEvent.AUDIO_LEVEL, level, Number(level)), this.log);
                this.audioMonitor.start(localStream);
            }
        });
        call.on('disconnect', () => {
            var _a;
            (_a = this.audioMonitor) === null || _a === void 0 ? void 0 : _a.stop();
            this.audioMonitor = null;
        });
        call.on('volume', (_inputVolume, outputVolume) => {
            if (outputVolume > REMOTE_AUDIO_THRESHOLD) {
                this.emit(WidgetStateEvent.REMOTE_AUDIO, outputVolume.toFixed(3), outputVolume);
            }
        });
        call.on('error', (error) => {
            var _a;
            const message = this.handleError(error, 'Call error');
            (_a = this.audioMonitor) === null || _a === void 0 ? void 0 : _a.stop();
            this.audioMonitor = null;
            this.emit(WidgetStateEvent.ERROR, message);
        });
        // Voice Insights: Network quality warnings
        call.on('warning', (warningName, warningData) => {
            this.log(`Call quality warning: ${warningName}`, warningData);
            // Surface quality warnings to the UI
            const warningMessage = CALL_QUALITY_WARNINGS[warningName] ||
                `Call quality issue: ${warningName}`;
            this.emit(WidgetStateEvent.WARNING, warningMessage);
        });
        // When warnings are cleared
        call.on('warning-cleared', (warningName) => {
            this.log(`Call quality warning cleared: ${warningName}`);
            this.emit(WidgetStateEvent.WARNING_CLEARED, warningName);
        });
    }
    /**
     * Handle errors centrally
     */
    handleError(error, context) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        this.log(`${context}: ${message}`, error);
        return message;
    }
    /**
     * Notify listeners about controller state changes.
     */
    emit(state, message, data) {
        const detail = { state, message, data };
        this.emitEvent(state, detail);
    }
    /**
     * Start a call
     */
    async startCall() {
        var _a;
        try {
            // Request microphone permission (using browser defaults)
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            stream.getTracks().forEach((track) => track.stop());
            // Initialize device if needed (lazy initialization)
            if (!this.twilioDevice) {
                await this.initializeTwilioDevice();
            }
            //  Make outbound call with iceServers
            const call = await this.twilioDevice.connect({
                params: { integration_id: this.config.integrationId },
                rtcConfiguration: {
                    iceServers: this.iceServers,
                },
            });
            this.activeCall = call;
            this.setupCallEventListeners(call);
            this.log('Call initiated', {
                callSid: (_a = call.parameters) === null || _a === void 0 ? void 0 : _a.CallSid,
                status: call.status(),
            });
        }
        catch (error) {
            const message = this.handleError(error, 'Failed to start call');
            this.emit(WidgetStateEvent.ERROR, message);
            throw error;
        }
    }
    /**
     * End the current call
     */
    endCall() {
        try {
            if (this.activeCall) {
                this.activeCall.disconnect();
            }
            else if (this.twilioDevice) {
                this.twilioDevice.disconnectAll();
            }
        }
        catch (error) {
            this.handleError(error, 'Error ending call');
        }
    }
    /**
     * Toggle mute/unmute for the current call
     */
    toggleMute() {
        try {
            if (this.activeCall) {
                const currentlyMuted = this.activeCall.isMuted();
                this.activeCall.mute(!currentlyMuted);
                this.log(currentlyMuted ? 'Call unmuted' : 'Call muted');
            }
        }
        catch (error) {
            this.handleError(error, 'Error toggling mute');
        }
    }
    /**
     * Check if the current call is muted
     */
    isMuted() {
        var _a, _b;
        return (_b = (_a = this.activeCall) === null || _a === void 0 ? void 0 : _a.isMuted()) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * Send DTMF digits through the active call
     */
    sendDigits(digits) {
        if (this.activeCall) {
            this.activeCall.sendDigits(digits);
            this.log('DTMF sent', { digits });
        }
    }
    /**
     * Destroy the audio widget and clean up
     */
    destroy() {
        var _a;
        // Stop audio monitoring
        (_a = this.audioMonitor) === null || _a === void 0 ? void 0 : _a.stop();
        this.audioMonitor = null;
        // Clear registered listeners
        this.eventListeners.clear();
        // End any active calls
        if (this.activeCall) {
            this.activeCall.disconnect();
        }
        // Destroy Twilio device
        if (this.twilioDevice) {
            this.twilioDevice.destroy();
            this.twilioDevice = null;
        }
        // Unmount React component and remove container
        if (this.widgetRoot) {
            this.widgetRoot.unmount();
            this.widgetRoot = null;
        }
        if (this.widgetContainer) {
            document.body.removeChild(this.widgetContainer);
            this.widgetContainer = null;
        }
    }
    /**
     * Subscribe to controller events.
     *
     * Returns a disposer that removes the handler when invoked.
     */
    on(event, handler) {
        var _a;
        const listeners = (_a = this.eventListeners.get(event)) !== null && _a !== void 0 ? _a : new Set();
        listeners.add(handler);
        this.eventListeners.set(event, listeners);
        return () => this.off(event, handler);
    }
    /**
     * Remove a previously registered event handler.
     */
    off(event, handler) {
        const listeners = this.eventListeners.get(event);
        if (!listeners) {
            return;
        }
        listeners.delete(handler);
        if (listeners.size === 0) {
            this.eventListeners.delete(event);
        }
    }
    /**
     * Invoke listeners for the specific event and the wildcard channel.
     */
    emitEvent(event, detail) {
        const invoke = (name) => {
            const listeners = this.eventListeners.get(name);
            if (!listeners || listeners.size === 0) {
                return;
            }
            listeners.forEach((listener) => {
                try {
                    listener(detail);
                }
                catch (error) {
                    const message = error instanceof Error ? error.message : 'Unknown listener error';
                    this.log(`Listener error for event "${name}": ${message}`, error);
                }
            });
        };
        invoke(event);
        invoke('any');
    }
}
